import { useMutation, useQueryClient } from '@tanstack/react-query';
import { customInstance } from '@/common/infrastructure/http/apiClient';
import { toBlock } from '@/blocks/application/mappers/blockMapper';
import type { BlockDto } from '@/blocks/application/mappers/blockMapper';
import type { Block } from '@/blocks/domain/types';
import { SUPPRESS_GLOBAL_ERROR_TOAST } from '@/common/application/constants/mutationMeta';
import { blocksQueryKey } from '@/blocks/application/hooks/useBlocks';

export interface DuplicateBlockSource {
  blockId: string;
}

/**
 * Duplicate a library block via `POST /blocks/{id}/duplicate`.
 * The backend copies the current published version as Published v1 on a new header.
 * A Draft-only block (no published version yet) answers 404, so the library does not offer Duplicate for it.
 */
export const useDuplicateBlock = () => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: SUPPRESS_GLOBAL_ERROR_TOAST,
    mutationFn: async ({ blockId }: DuplicateBlockSource): Promise<Block> => {
      const blockDto = await customInstance<BlockDto>({
        url: `/api/pdf-templates/v1/blocks/${blockId}/duplicate`,
        method: 'POST'
      });

      return toBlock(blockDto);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: blocksQueryKey,
        refetchType: 'all'
      });
    }
  });
};
